import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Flex, Row, Col, Skeleton, InputRef } from "antd";
import { WalletOutlined, DollarOutlined } from "@ant-design/icons";
import MetricCard from "components/MetricCard.component";
import { fetchAnalyticsRows, getClientRevenueAnalytics } from "hooks/queries/analytics";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { formatCurrency, formatUpgrades } from "shared/utils";
import { tnum } from "shared/components/TableText";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { AnalyticsRange, ClientRevenueAnalytics, ClientRevenueRow, CustomDayRange } from "shared/types";
import PATHS from "shared/constants/paths";
import { getYtdDayRange } from "shared/utils/dayjs";
import RangeSegmented from "./RangeSegmented";
import CustomRangePicker from "./CustomRangePicker";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { useAuth } from "context/AuthContextProvider";
import { useTableSort } from "hooks/useTableSort";
import { analyticsRangeStamp } from "shared/utils/download";
import { MAX_PDF_ROWS } from "pdf/layout";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { clientRevenueSpec } from "pdf/reports/specs/archetype2";
import { useReportPageCount } from "./useReportPageCount";
import { textPx } from "shared/constants/zoom";

// Financial counterpart to "Performa Pelanggan" (which stays operational-only
// under the Klien tab) — same split Paket/Membership already have between
// their own tab's "Performa" report and this tab's "Penjualan" report.
// "Pendapatan (Sesi)" (earned) and "Pendapatan (Pembelian)" (cash) are two
// different bases and are deliberately never summed into one displayed
// figure, including for the default sort order (see the backend).
const SalesClientsPage = () => {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { sortState, onSortChange } = useTableSort();
  // Defaults to YTD — customRange starts pre-populated to match so the first
  // render already queries {from,to} instead of a never-sent range=ytd token.
  const ytdRange = getYtdDayRange();
  const [range, setRange] = useState<AnalyticsRange>("ytd");
  const [customRange, setCustomRange] = useState<CustomDayRange>(ytdRange);
  const isYtdActive = customRange?.from === ytdRange.from && customRange?.to === ytdRange.to;
  const { data, isLoading } = getClientRevenueAnalytics(range, customRange);
  const searchInput = useRef<InputRef>(null);

  const columns: ReportColumn<ClientRevenueRow>[] = [
    reportColumn<ClientRevenueRow>("client", {
      dataIndex: "customer_name",
      search: getColumnSearchProps<ClientRevenueRow, "customer_name">({
        config: "customer_name",
        searchInput,
      }),
    }),
    reportColumn<ClientRevenueRow>("bookings", {
      dataIndex: "bookings",
      sorter: (a, b) => a.bookings - b.bookings,
    }),
    reportColumn<ClientRevenueRow>("booking_revenue", {
      dataIndex: "session_revenue",
      sorter: (a, b) => a.session_revenue - b.session_revenue,
    }),
    reportColumn<ClientRevenueRow>("packages_bought", {
      // Upgrades are counted separately: the client did not buy a second
      // package, but the fee they paid is inside Penjualan.
      dataIndex: "packages_bought",
      sorter: (a, b) => a.packages_bought - b.packages_bought,
    }),
    reportColumn<ClientRevenueRow>("memberships_bought", {
      dataIndex: "memberships_bought",
      sorter: (a, b) => a.memberships_bought - b.memberships_bought,
    }),
    // Always present. This table is row-capped (250 on screen, 1000 in the
    // export), so a column that appeared only when the visible rows held an
    // upgrade let the export disagree with the screen. A dash means this
    // client made no upgrades in the period; the fee for any they did make
    // is inside Penjualan beside it.
    reportColumn<ClientRevenueRow>("upgrades", {
      dataIndex: "upgrades",
      info: () => translate("analyticsPage.columns.upgrades_by_client_info"),
      render: (value: number) => <span style={tnum}>{formatUpgrades(value)}</span>,
      sorter: (a, b) => (a.upgrades ?? 0) - (b.upgrades ?? 0),
    }),
    reportColumn<ClientRevenueRow>("sales", {
      dataIndex: "purchase_revenue",
      sorter: (a, b) => a.purchase_revenue - b.purchase_revenue,
    }),
  ];

  // One source for both exports: the Excel query string and the PDF's
  // larger-row refetch must describe the same window.
  const windowParams = customRange
    ? { from_date: customRange.from, to_date: customRange.to }
    : { range };

  // The download fetches the full row set when the table holds only the
  // display-capped rows; the picker plans on that same set.
  const pageCount = useReportPageCount(clientRevenueSpec, data, sortState, {
    needsAll: !!data && data.table_total > data.table.length,
    loadAll: () => fetchAnalyticsRows<ClientRevenueAnalytics>("client", { ...windowParams, view: "revenue" }, MAX_PDF_ROWS),
  });

  return (
    <>
      <ReportPageHeading
        title={translate("analyticsPage.sales.report_clients")}
        backTo={PATHS.ANALYTICS}
        extra={
          <ExportMenu
            reportSlug="pendapatan-per-klien"
            params={windowParams}
            pdf={
              data && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  available: Math.min(data.table_total, MAX_PDF_ROWS),
                  total: data.table_total,
                },
                fileName: `spott-${"pendapatan-per-klien"}-${analyticsRangeStamp(range, customRange)}.pdf`,
                render: async (maxRows) => {
                  // The page holds only the display-capped rows, so a bigger
                  // ask needs its own request; fall back to what we have if
                  // that fetch fails rather than losing the download.
                  const source =
                    (pageCount.source !== data ? pageCount.source : undefined) ??
                    (maxRows && maxRows > data.table.length
                      ? (await fetchAnalyticsRows<ClientRevenueAnalytics>(
                          "client",
                          { ...windowParams, view: "revenue" },
                          maxRows,
                        )) ?? data
                      : data);
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: "pendapatan-per-klien",
                    studio: authState.business?.name ?? "",
                    data: source,
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>
            {data?.range_label}
          </span>
          <Flex align="center" gap={10} wrap="wrap">
            <CustomRangePicker value={isYtdActive ? null : customRange} onChange={setCustomRange} />
            <RangeSegmented
              value={isYtdActive ? "ytd" : range}
              active={!customRange || isYtdActive}
              onChange={(value) => {
                setCustomRange(value === "ytd" ? ytdRange : null);
                setRange(value);
              }}
            />
          </Flex>
        </Flex>
        <Row gutter={[16, 16]}>
          {isLoading ? (
            Array.from({ length: 2 }).map((_, i) => (
              <Col key={i} xs={24} sm={12}>
                <Card style={{ borderRadius: 16 }}>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))
          ) : (
            <>
              <Col xs={24} sm={12}>
                <MetricCard
                  icon={<DollarOutlined />}
                  title={translate("analyticsPage.sales.kpi_total_revenue")}
                  information={translate("analyticsPage.columns.booking_revenue_info")}
                  value={formatCurrency(data?.total_session_revenue.value ?? 0)}
                />
              </Col>
              <Col xs={24} sm={12}>
                <MetricCard
                  icon={<WalletOutlined />}
                  title={translate("analyticsPage.sales.kpi_total_sales")}
                  information={translate("analyticsPage.columns.sales_info")}
                  value={formatCurrency(data?.total_purchase_revenue.value ?? 0)}
                />
              </Col>
            </>
          )}
        </Row>
        <Card styles={{ body: { padding: 18 } }}>
          <ReportTable<ClientRevenueRow>
            serverTotal={{ total: data?.table_total, noun: translate("pdf.noun.klien") }}
            rowKey="customer_id"
            loading={isLoading}
            columns={columns}
            dataSource={data?.table ?? []}
            pageMemory="analyticsSalesClients"
            onSortChange={onSortChange}
            onRow={(row) => ({
              onClick: () => navigate(`${PATHS.CUSTOMER}/${row.customer_id}?tab=1`),
              style: { cursor: "pointer" },
            })}
          />
        </Card>
      </Flex>
    </>
  );
};

export default SalesClientsPage;
